import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Search, ChevronRight, BookOpen, Zap, Swords, MapPin,
    Trophy, Star, Award, Shield, Sparkles, MessageCircle,
    ArrowRight, ExternalLink, Home
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import CustomCursor from './CustomCursor';

const WikiPage = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState('');
    const [openAccordions, setOpenAccordions] = useState({});
    const [selectedCategory, setSelectedCategory] = useState('all');

    const toggleAccordion = (id) => {
        setOpenAccordions(prev => ({
            ...prev,
            [id]: !prev[id]
        }));
    };

    const categories = [
        { id: 'all', name: 'Tout Voir', icon: <BookOpen className="w-5 h-5" />, color: '#DC0A2D' },
        { id: 'start', name: 'Débuter', icon: <Star className="w-5 h-5" />, color: '#FFCB05' },
        { id: 'pokemon', name: 'Pokémon', icon: <Sparkles className="w-5 h-5" />, color: '#DC0A2D' },
        { id: 'combat', name: 'Combat', icon: <Swords className="w-5 h-5" />, color: '#3B4CCA' },
        { id: 'world', name: 'Monde', icon: <MapPin className="w-5 h-5" />, color: '#FFCB05' },
        { id: 'events', name: 'Events', icon: <Trophy className="w-5 h-5" />, color: '#DC0A2D' }
    ];

    const wikiSections = [
        {
            id: 'rules',
            category: 'start',
            title: '📜 Règlement du Serveur',
            color: '#DC0A2D',
            icon: <Shield className="w-6 h-6" />,
            items: [
                {
                    title: 'Respect de la Communauté',
                    content: 'Aucune forme de harcèlement, racisme, sexisme ou insultes ne sera tolérée. Gardons une ambiance friendly !'
                },
                {
                    title: 'Pas de Triche',
                    content: 'Les mods, hacks ou exploits donnant un avantage injuste sont strictement interdits. Fair-play avant tout !'
                },
                {
                    title: 'Respect des Builds',
                    content: 'Ne griefe pas, ne vole pas et respecte les constructions des autres joueurs. Demande toujours avant de modifier.'
                },
                {
                    title: 'Langage Approprié',
                    content: 'Garde un langage correct dans le chat. Les spams et caps lock excessifs sont interdits.'
                },
                {
                    title: 'Zones PvP',
                    content: 'Le PvP est autorisé uniquement dans les zones dédiées. Respecte les joueurs qui ne veulent pas combattre.'
                }
            ]
        },
        {
            id: 'commands',
            category: 'start',
            title: '⚡ Commandes Essentielles',
            color: '#FFCB05',
            icon: <Zap className="w-6 h-6" />,
            items: [
                {
                    title: '/spawn',
                    content: 'Te téléporte au spawn principal du serveur. Utilise-la pour retourner au hub à tout moment.'
                },
                {
                    title: '/home set [nom]',
                    content: 'Définit un point de téléportation personnel. Tu peux avoir jusqu\'à 3 homes. Exemple : /home set base'
                },
                {
                    title: '/home [nom]',
                    content: 'Te téléporte à l\'un de tes homes. Exemple : /home base'
                },
                {
                    title: '/tpa [joueur]',
                    content: 'Demande une téléportation vers un autre joueur. Il doit accepter avec /tpaccept'
                },
                {
                    title: '/pokeheal',
                    content: 'Soigne tous tes Pokémon instantanément. Cooldown de 10 minutes.'
                },
                {
                    title: '/trade [joueur]',
                    content: 'Initie un échange de Pokémon avec un autre joueur. Interface sécurisée garantie !'
                },
                {
                    title: '/pc',
                    content: 'Ouvre ton PC Pokémon à distance pour gérer ton équipe et ton stockage.'
                },
                {
                    title: '/checkspawns',
                    content: 'Affiche les Pokémon qui peuvent spawn dans ta zone actuelle.'
                }
            ]
        },
        {
            id: 'pokemon',
            category: 'pokemon',
            title: '✨ Guide Pokémon',
            color: '#DC0A2D',
            icon: <Sparkles className="w-6 h-6" />,
            items: [
                {
                    title: 'Comment Capturer',
                    content: 'Craft des Pokéballs (Apricorns + Iron disc), affaiblis le Pokémon en combat, puis lance ta ball ! Plus le Pokémon est faible, plus tes chances sont élevées.'
                },
                {
                    title: 'Pokémon Shinies',
                    content: 'Taux de spawn : 1/4096 en normal, 1/512 avec le Shiny Charm. Les shinies ont des particules spéciales et sont ultra rares !'
                },
                {
                    title: 'Évolutions',
                    content: 'Montée en niveau, pierres évolutives, échanges, bonheur... Chaque Pokémon évolue différemment. Consulte notre Pokédex en jeu !'
                },
                {
                    title: 'Reproduction',
                    content: 'Place deux Pokémon compatibles dans un ranch avec un Cake. Récupère un œuf après quelques minutes. Les IVs sont hérités !'
                },
                {
                    title: 'EV & IV Training',
                    content: 'Les IVs sont générés aléatoirement (0-31). Les EVs se gagnent en combattant des Pokémon (max 252 par stat). Utilise les Power Items pour accélérer !'
                },
                {
                    title: 'Talents Cachés',
                    content: 'Certains Pokémon ont des talents cachés rares. Trouve-les dans des raids ou via reproduction avec un parent ayant le talent.'
                }
            ]
        },
        {
            id: 'combat',
            category: 'combat',
            title: '⚔️ Système de Combat',
            color: '#3B4CCA',
            icon: <Swords className="w-6 h-6" />,
            items: [
                {
                    title: 'Types & Efficacités',
                    content: 'Le tableau des types est identique aux jeux Pokémon officiels. Feu > Plante, Eau > Feu, Plante > Eau, etc. Maîtrise les matchups !'
                },
                {
                    title: 'Combat PvE',
                    content: 'Affronte des Pokémon sauvages pour gagner de l\'XP. Les Pokémon alphas sont plus forts et donnent plus de récompenses !'
                },
                {
                    title: 'Combat PvP',
                    content: 'Défie d\'autres joueurs en 1v1, 2v2 ou 3v3. Les combats classés sont disponibles dans l\'arène principale !'
                },
                {
                    title: 'Arènes & Champions',
                    content: '8 arènes thématiques à travers le monde. Bats les champions pour gagner des badges et débloquer la Ligue !'
                },
                {
                    title: 'Raids Dynamax',
                    content: 'Rejoins des raids à 4 joueurs pour affronter des Pokémon Dynamax ultra puissants. Récompenses légendaires garanties !'
                },
                {
                    title: 'Stratégies Avancées',
                    content: 'Hazards (Spikes, Stealth Rock), Weather effects (Soleil, Pluie), Trick Room... Découvre la méta compétitive !'
                }
            ]
        },
        {
            id: 'world',
            category: 'world',
            title: '🗺️ Explorer le Monde',
            color: '#FFCB05',
            icon: <MapPin className="w-6 h-6" />,
            items: [
                {
                    title: 'Biomes Pokémon',
                    content: '40+ biomes uniques ! Chaque biome a son propre pool de spawns. Montagne = Pokémon roche, Océan = Pokémon eau, etc.'
                },
                {
                    title: 'Structures Customs',
                    content: 'Découvre des Labs abandonnés, des Tours Pokémon, des Caves mystérieuses... Chacune contient des trésors et Pokémon rares !'
                },
                {
                    title: 'Dimensions',
                    content: 'Nether et End accessibles avec des spawns Pokémon adaptés. Trouve des légendaires exclusifs dans ces dimensions !'
                },
                {
                    title: 'Temples & Sanctuaires',
                    content: 'Des temples dédiés aux légendaires apparaissent aléatoirement. Résous les énigmes pour invoquer le boss !'
                },
                {
                    title: 'Zones Exclusives',
                    content: 'Safari Zone, Contest Hall, Battle Frontier... Des zones spéciales avec des mécaniques uniques !'
                },
                {
                    title: 'Villes Principales',
                    content: '6 villes majeures avec shops, PokéCentres, arènes et mini-jeux. Chacune a son identité et ses quêtes !'
                }
            ]
        },
        {
            id: 'events',
            category: 'events',
            title: '🏆 Events & Activités',
            color: '#DC0A2D',
            icon: <Trophy className="w-6 h-6" />,
            items: [
                {
                    title: 'Tournois Hebdomadaires',
                    content: 'Chaque samedi à 20h, tournoi PvP en bracket éliminatoire. Récompenses : Shinies garantis, Master Balls, cosmétiques rares !'
                },
                {
                    title: 'Chasses Communautaires',
                    content: 'Tous les dimanches, un Pokémon est boosté avec un taux shiny x10. Profite de ces events pour compléter ta collection !'
                },
                {
                    title: 'Raids Légendaires',
                    content: 'Des légendaires spawns aléatoirement dans le monde. Le premier à le trouver et le capturer le garde ! Annonces Discord.'
                },
                {
                    title: 'Concours Beauté',
                    content: 'Montre tes plus beaux Pokémon dans des contests thématiques. Les gagnants reçoivent des rubans exclusifs !'
                },
                {
                    title: 'Événements Saisonniers',
                    content: 'Halloween, Noël, Été... Des events saisonniers avec des Pokémon et cosmétiques limités !'
                },
                {
                    title: 'Quêtes Journalières',
                    content: 'Complète des quêtes quotidiennes pour gagner des Poké$, des items rares et de l\'XP boostée !'
                }
            ]
        },
        {
            id: 'economy',
            category: 'start',
            title: '💰 Économie & Shop',
            color: '#3B4CCA',
            icon: <Award className="w-6 h-6" />,
            items: [
                {
                    title: 'Gagner des Poké$',
                    content: 'Battez des dresseurs, complétez des quêtes, vendez vos items, participez aux events. Pas de pay-to-win !'
                },
                {
                    title: 'Shop Principal',
                    content: 'Pokéballs, Potions, TMs, Pierres évolutives... Tout le nécessaire est disponible en jeu avec de la monnaie in-game.'
                },
                {
                    title: 'Marché Joueurs',
                    content: 'Achète et vends des Pokémon, items et services avec d\'autres joueurs. Economy 100% player-driven !'
                },
                {
                    title: 'Cosmétiques',
                    content: 'Débloques des skins, particules, titres et badges en jouant. Certains sont limités aux événements !'
                }
            ]
        },
        {
            id: 'faq',
            category: 'start',
            title: '❓ FAQ - Questions Fréquentes',
            color: '#FFCB05',
            icon: <MessageCircle className="w-6 h-6" />,
            items: [
                {
                    title: 'Puis-je transférer mes Pokémon d\'un autre serveur ?',
                    content: 'Non, pour des raisons d\'équilibre. Tout le monde commence avec les mêmes chances !'
                },
                {
                    title: 'Y a-t-il du pay-to-win ?',
                    content: 'Absolument pas ! Tous les avantages s\'obtiennent en jouant. Les dons servent uniquement à supporter les frais du serveur.'
                },
                {
                    title: 'Comment signaler un bug ?',
                    content: 'Utilise /report en jeu ou ouvre un ticket sur notre Discord. Notre équipe intervient rapidement !'
                },
                {
                    title: 'Puis-je avoir plusieurs comptes ?',
                    content: 'Non, un seul compte par joueur pour éviter les abus. Les multi-comptes sont bannis.'
                },
                {
                    title: 'Le serveur wipe-t-il ?',
                    content: 'Jamais de wipe complet ! Nous faisons des mises à jour régulières mais ta progression est sauvegardée.'
                },
                {
                    title: 'Comment devenir Helper/Mod ?',
                    content: 'Sois actif, aide la communauté et respecte les règles. Les recrutements sont annoncés sur Discord !'
                }
            ]
        }
    ];

    const filteredSections = selectedCategory === 'all'
        ? wikiSections
        : wikiSections.filter(section => section.category === selectedCategory);

    const quickLinks = [
        { title: 'Guide Débutant', icon: '📚', link: '#start', color: '#DC0A2D' },
        { title: 'Commandes', icon: '⚡', link: '#commands', color: '#FFCB05' },
        { title: 'Pokédex Complet', icon: '📖', link: '#', color: '#3B4CCA' },
        { title: 'Arènes & Ligue', icon: '🏆', link: '#combat', color: '#DC0A2D' },
        { title: 'Map Interactive', icon: '🗺️', link: '#', color: '#FFCB05' },
        { title: 'Discord Support', icon: '💬', link: 'https://discord.gg/3wS59h3Vbu', color: '#3B4CCA' }
    ];

    return (
        <div className="relative min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50 overflow-hidden">
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&family=Nunito:wght@400;600;700;800&display=swap');
        
        .wiki-gradient {
          background: linear-gradient(135deg, #DC0A2D 0%, #FFCB05 50%, #3B4CCA 100%);
        }
        
        .accordion-content {
          overflow: hidden;
          transition: max-height 0.3s ease-in-out;
        }
        
        .search-glow {
          box-shadow: 0 0 0 4px rgba(220, 10, 45, 0.1);
          transition: all 0.3s;
        }
        
        .search-glow:focus {
          box-shadow: 0 0 0 4px rgba(220, 10, 45, 0.3);
        }
        
        .category-pill {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .category-pill:hover {
          transform: translateY(-2px);
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        
        .float-animation {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>

            {/* Custom Pokeball Cursor */}
            <CustomCursor />

            <motion.nav
                initial={{ y: -100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6 }}
                className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-xl border-b-4 border-red-600 shadow-xl"
            >
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    <motion.a
                        href="/"
                        className="flex items-center gap-2 px-6 py-3 rounded-full bg-red-600 text-white font-bold hover:bg-red-700 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                    >
                        <Home className="w-5 h-5" />
                        Retour Accueil
                    </motion.a>

                    <div className="flex items-center gap-4">
                        <div className="relative w-10 h-10">
                            {/* Pokeball Logo */}
                            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-red-600 to-red-700 shadow-2xl"></div>
                            <div className="absolute inset-0 top-0 h-1/2 rounded-t-full bg-red-600"></div>
                            <div className="absolute inset-0 bottom-0 top-1/2 h-1/2 rounded-b-full bg-white"></div>
                            <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-900 transform -translate-y-1/2"></div>
                            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border border-gray-900"></div>
                        </div>
                        <div>
                            <div className="text-3xl font-black text-red-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                PokeMovie
                            </div>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider">Serveur Cobblemon</div>
                        </div>
                    </div>
                </div>
            </motion.nav>

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-red-600 via-red-700 to-red-800 text-white pt-32 pb-20 px-6">
                <div className="absolute inset-0 opacity-5">
                    <div className="absolute top-20 left-20 w-64 h-64 border-8 border-white rounded-full"></div>
                    <div className="absolute bottom-10 right-10 w-96 h-96 border-8 border-white rounded-full"></div>
                </div>

                <div className="max-w-6xl mx-auto relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center"
                    >
                        <motion.div
                            initial={{ scale: 0, rotate: -180 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                            className="inline-block mb-8"
                        >
                            <div className="text-8xl float-animation">📚</div>
                        </motion.div>

                        <h1 className="text-5xl lg:text-7xl font-black mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Wiki <span className="text-yellow-400">PokeMovie</span>
                        </h1>

                        <p className="text-2xl text-red-100 mb-12 max-w-3xl mx-auto font-semibold">
                            Tout ce que tu dois savoir pour devenir un Maître Pokémon !
                        </p>

                        {/* Search Bar */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.4 }}
                            className="max-w-2xl mx-auto mb-12"
                        >
                            <div className="relative">
                                <Search className="absolute left-6 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-400" />
                                <input
                                    type="text"
                                    placeholder="Rechercher dans le wiki... (ex: 'comment capturer', 'commandes')"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-16 pr-6 py-5 rounded-2xl text-gray-900 font-semibold text-lg search-glow focus:outline-none"
                                />
                            </div>
                        </motion.div>

                        {/* Quick Links */}
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                            {quickLinks.map((link, i) => (
                                <motion.a
                                    key={link.title}
                                    href={link.link}
                                    target="_blank"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.6 + i * 0.1 }}
                                    whileHover={{ scale: 1.05, y: -3 }}
                                    className="p-4 rounded-xl bg-white/10 backdrop-blur-sm border-2 border-white/30 hover:bg-white/20 transition-all"
                                >
                                    <div className="text-3xl mb-2">{link.icon}</div>
                                    <div className="text-sm font-bold">{link.title}</div>
                                </motion.a>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Category Filter */}
            <section className="sticky top-0 z-40 bg-white border-b-4 border-gray-200 shadow-lg">
                <div className="max-w-6xl mx-auto px-6 py-4">
                    <div className="flex flex-wrap gap-3 justify-center">
                        {categories.map((cat) => (
                            <motion.button
                                key={cat.id}
                                onClick={() => setSelectedCategory(cat.id)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className={`category-pill flex items-center gap-2 px-6 py-3 rounded-full font-bold transition-all ${
                                    selectedCategory === cat.id
                                        ? 'text-white shadow-xl'
                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                style={{
                                    backgroundColor: selectedCategory === cat.id ? cat.color : undefined
                                }}
                            >
                                {cat.icon}
                                <span>{cat.name}</span>
                            </motion.button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Wiki Content */}
            <section className="py-16 px-6">
                <div className="max-w-6xl mx-auto">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={selectedCategory}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ duration: 0.3 }}
                            className="space-y-8"
                        >
                            {filteredSections.map((section, sectionIndex) => (
                                <motion.div
                                    key={section.id}
                                    initial={{ opacity: 0, y: 30 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: sectionIndex * 0.1 }}
                                    className="bg-white rounded-3xl shadow-xl border-4 border-gray-100 overflow-hidden"
                                >
                                    {/* Section Header */}
                                    <div
                                        className="p-8 text-white"
                                        style={{ background: `linear-gradient(135deg, ${section.color}, ${section.color}dd)` }}
                                    >
                                        <div className="flex items-center gap-4">
                                            <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center">
                                                {section.icon}
                                            </div>
                                            <h2 className="text-3xl lg:text-4xl font-black" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                                {section.title}
                                            </h2>
                                        </div>
                                    </div>

                                    {/* Section Content */}
                                    <div className="p-8">
                                        <div className="space-y-4">
                                            {section.items.map((item, itemIndex) => (
                                                <motion.div
                                                    key={itemIndex}
                                                    initial={{ opacity: 0, x: -20 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: itemIndex * 0.05 }}
                                                    className="border-2 border-gray-200 rounded-2xl overflow-hidden hover:border-red-600 transition-all"
                                                >
                                                    <button
                                                        onClick={() => toggleAccordion(`${section.id}-${itemIndex}`)}
                                                        className="w-full p-6 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-all"
                                                    >
                                                        <div className="flex items-center gap-4">
                                                            <motion.div
                                                                animate={{ rotate: openAccordions[`${section.id}-${itemIndex}`] ? 90 : 0 }}
                                                                transition={{ duration: 0.3 }}
                                                            >
                                                                <ChevronRight className="w-6 h-6 text-red-600" />
                                                            </motion.div>
                                                            <span className="text-xl font-black text-gray-900 text-left" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                                {item.title}
                              </span>
                                                        </div>
                                                    </button>

                                                    <AnimatePresence>
                                                        {openAccordions[`${section.id}-${itemIndex}`] && (
                                                            <motion.div
                                                                initial={{ height: 0, opacity: 0 }}
                                                                animate={{ height: 'auto', opacity: 1 }}
                                                                exit={{ height: 0, opacity: 0 }}
                                                                transition={{ duration: 0.3 }}
                                                                className="accordion-content"
                                                            >
                                                                <div className="p-6 bg-white border-t-2 border-gray-200">
                                                                    <p className="text-lg text-gray-700 leading-relaxed font-semibold">
                                                                        {item.content}
                                                                    </p>
                                                                </div>
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </motion.div>
                    </AnimatePresence>
                </div>
            </section>

            {/* Help CTA */}
            <section className="py-20 px-6 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 text-white">
                <div className="max-w-4xl mx-auto text-center">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                    >
                        <div className="text-6xl mb-6">💬</div>
                        <h2 className="text-4xl lg:text-5xl font-black mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            Besoin d'Aide ?
                        </h2>
                        <p className="text-xl text-blue-100 mb-8 font-semibold max-w-2xl mx-auto">
                            Tu ne trouves pas la réponse à ta question ? Notre équipe de support et la communauté sont là pour t'aider !
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4 justify-center">
                            <motion.a
                                href="https://discord.gg/3wS59h3Vbu"
                                target="_blank" rel="noreferrer"
                                whileHover={{ scale: 1.05, y: -3 }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl bg-white text-blue-600 font-black text-xl shadow-2xl hover:bg-yellow-400 hover:text-blue-700 transition-all"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            >
                                <MessageCircle className="w-6 h-6" />
                                Rejoindre Discord
                                <ArrowRight className="w-6 h-6" />
                            </motion.a>
                            <motion.a
                                href="#"
                                whileHover={{ scale: 1.05, y: -3 }}
                                whileTap={{ scale: 0.95 }}
                                className="inline-flex items-center gap-3 px-10 py-5 rounded-2xl bg-white/10 backdrop-blur-sm border-4 border-white/30 text-white font-black text-xl hover:bg-white/20 transition-all"
                                style={{ fontFamily: 'Montserrat, sans-serif' }}
                            >
                                <ExternalLink className="w-6 h-6" />
                                Ouvrir un Ticket
                            </motion.a>
                        </div>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default WikiPage;